import { useAppDispatch, useAppSelector } from 'hooks/redux';
import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import SnackbarContent from '@mui/material/SnackbarContent';
import Typography from '@mui/material/Typography';

import ApplyToExistingTxnDialog from './ApplyToExistingTxnDialog';
import RulesList from './RulesList';

import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { rootUiActions } from 'app-components/flux/root-ui';
import { clearState } from 'app-components/flux/transaction-rules/transactionRulesForm';
import { getRules } from 'app-components/flux/transaction-rules/transactionRulesSelectors';
import {
  transactionRulesActions,
  transactionRulesSelectors,
} from 'app-components/flux/transaction-rules/transactionRulesSlice';
import type { TransactionRuleType } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import { useFetchOnce } from 'app-components/hooks/utils';
import { tracker } from 'app-components/utils/core';
import { deleteTransactionRuleDialog } from 'components/Dialogs/DeleteTransactionRule/actions';

import { TransactionRuleAction } from 'components/Dialogs/TransactionRules/actions';
import SettingsHeader from 'containers/Settings/SharedComponents/SettingsHeader';
import { addNotifications, removeNotifications } from 'data/notifications/notificationsActions';
import { mkNotification } from 'data/notifications/notificationsTypes';
import { OrderedMap } from 'immutable';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  mainContent: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: 'auto',
  },
}));

const RulesSettings = () => {
  const { classes } = useStyles();
  const [rule, setRule] = useState<TransactionRuleType>();
  const [showApplyToExistingTransactions, setShowApplyToExistingTransactions] = useState<boolean>(false);
  const [dontShowRuleCreatedDlg, setDontShowRuleCreatedDlg] = usePreferenceV2('dontShowRuleCreatedDlg', true);
  const dialogId = 'settings-rules-editor';
  const [snackBarMsg, setSnackBarMsg] = useState<string>();
  const dispatch = useAppDispatch();

  const rules = useAppSelector((state) => getRules(state));

  useFetchOnce(transactionRulesActions.fetch, transactionRulesSelectors);

  useEffect(() => {
    tracker.track(tracker.events.txnRulesList, { source: 'settings' });
  }, []);

  useEffect(() => {
    if (snackBarMsg && !showApplyToExistingTransactions) {
      const id = uuidv4();

      const notifications = OrderedMap([
        [
          id,
          mkNotification({
            id,
            snackbarContent: SnackbarContent,
            snackbarContentProps: {
              message: <Typography>{snackBarMsg}</Typography>,
              action: (
                <IconButton
                  size='small'
                  aria-label='dismiss'
                  id='outdated-browser-dismiss'
                  color='inherit'
                  onClick={() => {
                    setSnackBarMsg(undefined);
                    dispatch(removeNotifications([id]));
                  }}
                >
                  <CloseIcon />
                </IconButton>
              ),
            },
          }),
        ],
      ]);
      dispatch(addNotifications(notifications));
    }
  }, [dispatch, showApplyToExistingTransactions, snackBarMsg]);

  // delete a rule
  const handleDeleteRule = useCallback(
    (id: string | undefined) => {
      id && dispatch(deleteTransactionRuleDialog({ ruleId: id, source: 'ruleList' }));
    },
    [dispatch],
  );

  const showNotificationModal = useCallback(
    (rule: TransactionRuleType) => {
      !dontShowRuleCreatedDlg && rule.applyToExistingTransactions && setShowApplyToExistingTransactions(true);
    },
    [dontShowRuleCreatedDlg],
  );

  const handleClose = useCallback(
    (newRule: TransactionRuleType) => {
      let snackbarText;

      if (newRule && newRule.id) {
        showNotificationModal(newRule);
        setRule(newRule);
        snackbarText = newRule.clientId ? 'Rule replaced' : 'Rule updated';
        dontShowRuleCreatedDlg && setSnackBarMsg(snackbarText);
      }

      dispatch(rootUiActions.removeDialog(dialogId));
    },
    [dispatch, dontShowRuleCreatedDlg, showNotificationModal],
  );

  // setting the rule opens the drawer
  const handleOpenEditor = useCallback(
    (id: string | undefined) => {
      const foundRule = id ? rules.find((rule) => rule.id === id) : clearState;
      if (foundRule) {
        dispatch(
          TransactionRuleAction({
            id: dialogId,
            rule: foundRule,
            handleClose,
          }),
        );

        const source = foundRule.id ? 'settingsEdit' : 'settingsCreate';
        tracker.track(tracker.events.txnRuleStarted, { source });
      }
    },
    [dispatch, handleClose, rules],
  );

  return (
    <>
      <SettingsHeader
        title='Rules'
        primaryItem={
          <Button
            id='create-rule-button'
            variant='contained'
            startIcon={<AddCircleRoundedIcon />}
            onClick={() => handleOpenEditor(undefined)}
          >
            Rule
          </Button>
        }
      />
      <Box className={classes.mainContent}>
        {/* <RulesTemplate /> */}
        <RulesList
          rules={rules}
          onEditCallback={handleOpenEditor}
          onDeleteCallback={handleDeleteRule}
          // onDuplicateCallback={handleDuplicteRule}
        />
      </Box>

      <ApplyToExistingTxnDialog
        title={rule?.id && rule?.clientId ? 'Rule replaced' : rule?.id ? 'Rule updated' : 'Rule created'}
        open={showApplyToExistingTransactions}
        onAccept={() => {
          setShowApplyToExistingTransactions(false);
        }}
        onCancel={() => {
          setShowApplyToExistingTransactions(false);
        }}
        showDlg={dontShowRuleCreatedDlg}
        onShowDlgChange={setDontShowRuleCreatedDlg}
      />
    </>
  );
};

export default RulesSettings;
